import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { Button } from "../ui/button";
import { api, errMessage } from "../../lib/api";

// Consent renewal reminder: appears only when a child's yearly voice-consent
// renewal is due soon (or overdue). Parents can renew in one tap or withdraw
// voice processing — non-voice learning always continues.
export const ConsentRenewal = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = () => api.get("/consent/renewal-status")
    .then(({ data: d }) => { setData(d); setFailed(false); })
    .catch(() => setFailed(true));
  useEffect(() => { load(); }, []);

  if (failed) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="consent-renewal-error">
        Couldn't check consent renewal status right now — it will retry on your next visit.
      </p>
    );
  }
  const due = (data?.children || []).filter((c) => c.due_soon || c.overdue);
  if (due.length === 0) return null;

  const renew = async (childId) => {
    try {
      await api.post(`/consent/${childId}/renew`);
      toast.success("Consent renewed — thank you for checking in!");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };

  const withdrawVoice = async (childId) => {
    if (!window.confirm("Turn off voice processing? Practice becomes listen-only; all other learning continues.")) return;
    try {
      await api.post(`/consent/${childId}/withdraw-voice`);
      toast.success("Voice processing is off — learning continues in listen-only mode");
      load();
    } catch (e) { toast.error(errMessage(e)); }
  };

  return (
    <section className="bg-amber-50 border border-amber-200 rounded-2xl p-5 space-y-3"
      data-testid="consent-renewal-banner">
      <h3 className="text-sm font-bold flex items-center gap-2 text-amber-900">
        <CalendarClock className="h-4 w-4" aria-hidden="true" /> Yearly consent check-in
      </h3>
      {due.map((c) => (
        <div key={c.child_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/70 border border-amber-100 p-3"
          data-testid={`consent-renewal-${c.child_id}`}>
          <p className="text-xs text-amber-900 min-w-0">
            <b>{c.display_name}</b>: consent {c.overdue
              ? <>renewal is <b>overdue</b></>
              : <>renews in <b>{c.days_left} day{c.days_left === 1 ? "" : "s"}</b></>}
            {" "}· voice practice is currently <b>{c.voice_on ? "ON" : "OFF"}</b>.
            Review, renew, or withdraw — your choice, any time.
          </p>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" onClick={() => renew(c.child_id)}
              className="rounded-full h-8 text-xs" data-testid={`consent-renew-${c.child_id}`}>
              Renew consent
            </Button>
            {c.voice_on && (
              <Button size="sm" variant="outline" onClick={() => withdrawVoice(c.child_id)}
                className="rounded-full h-8 text-xs" data-testid={`consent-withdraw-voice-${c.child_id}`}>
                Turn off voice
              </Button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
};
